import React, { useState, useEffect } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    ReferenceInput,
    SelectInput,
    BooleanInput,
    required,
    useTranslate,
    Toolbar,
    SaveButton,
    useRedirect,
    useDataProvider,
    useNotify,
    useGetIdentity,
    regex,
    minLength,
} from 'react-admin';
import {
    Box,
    Typography,
    Alert,
    Card,
    CardContent,
    IconButton,
    Chip,
    Autocomplete,
    TextField as MuiTextField,
    Checkbox,
} from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { CreditCard, Person, LocationCity, Settings, ArrowBack, Info } from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const RFID_CARD_KNOWN_FIELDS = new Set([
    'cardNumber', 'cardType', 'appUserId', 'rfidUserId', 'vendorId', 'siteIds',
    'validFrom', 'validUntil', 'maxUses', 'isActive', 'isBlocked', 'blockedReason',
]);

const mapRfidCardBackendPath = (path: string): string => {
    const stripped = path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');
    // Zod `.refine()` on the (appUserId, rfidUserId) pair emits the dot-joined
    // compound path "appUserId.rfidUserId". Map it to `rfidUserId` (the more
    // visible input in the form) so the error highlights an input.
    if (stripped === 'appUserId.rfidUserId') {
        return 'rfidUserId';
    }
    return stripped;
};

const rfidCardFieldLabelKey = (field: string): string =>
    RFID_CARD_KNOWN_FIELDS.has(field)
        ? `resources.rfid-cards.fields.${field}`
        : field;

const CreateToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': {
                    backgroundColor: '#00A843',
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/rfid-cards')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.rfid-cards.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.rfid-cards.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const UserSelection = ({ vendorId, onChange }: { vendorId: string | null; onChange: (userId: string | null) => void }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [users, setUsers] = useState<any[]>([]);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (vendorId) {
            loadVendorUsers();
        } else {
            setUsers([]);
            setSelectedUser(null);
            onChange(null);
        }
    }, [vendorId]);

    const loadVendorUsers = async () => {
        try {
            setLoading(true);
            // Get vendor details to get RFID users
            const { data: vendor } = await dataProvider.getOne('vendors', { id: vendorId });

            console.log('Vendor data loaded:', vendor);
            console.log('RFID Users:', vendor.rfidUsers);

            if (vendor.rfidUsers && vendor.rfidUsers.length > 0) {
                setUsers(vendor.rfidUsers);
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error('Error loading vendor users:', error);
            notify('Error loading vendor users', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleUserChange = (event: any) => {
        const userId = event.target.value;
        setSelectedUser(userId);
        onChange(userId);
    };

    if (!vendorId) {
        return (
            <SelectInput
                source="rfidUserId"
                label={translate('resources.rfid-cards.fields.userId')}
                choices={[]}
                fullWidth
                disabled
                helperText={translate('resources.rfid-cards.create.selectVendorFirst') ||
                          'Please select a vendor first'}
            />
        );
    }

    if (loading) {
        return (
            <SelectInput
                source="rfidUserId"
                label={translate('resources.rfid-cards.fields.userId')}
                choices={[]}
                fullWidth
                disabled
                helperText="Loading users..."
            />
        );
    }

    console.log('UserSelection rendering with users:', users, 'disabled:', users.length === 0);

    return (
        <SelectInput
            source="rfidUserId"
            label={translate('resources.rfid-cards.fields.userId')}
            choices={users.map(user => ({
                id: user.userId,
                name: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unnamed User'
            }))}
            validate={required()}
            fullWidth
            helperText={
                users.length === 0
                    ? translate('resources.rfid-cards.create.noUsersAvailable') || 'No RFID users available for this vendor'
                    : translate('resources.rfid-cards.create.userHelperText') || 'Select the employee who will use this card'
            }
            disabled={users.length === 0}
            emptyText={translate('resources.rfid-cards.create.noUsersAvailable') || 'No RFID users available for this vendor'}
            onChange={handleUserChange}
        />
    );
};

const SiteSelection = ({ vendorId }: { vendorId: string | null }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [sites, setSites] = useState<any[]>([]);
    const [selectedSites, setSelectedSites] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (vendorId) {
            loadVendorSites();
        } else {
            setSites([]);
            setSelectedSites([]);
        }
    }, [vendorId]);

    const loadVendorSites = async () => {
        try {
            setLoading(true);
            // Get vendor details to find allowed sites
            const { data: vendor } = await dataProvider.getOne('vendors', { id: vendorId });

            if (vendor.allowedSites && vendor.allowedSites.length > 0) {
                // Get site details for allowed sites
                const { data: allSites } = await dataProvider.getList('sites', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { ids: vendor.allowedSites },
                });
                setSites(allSites);
            } else {
                setSites([]);
            }
        } catch (error) {
            notify('Error loading vendor sites', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSiteChange = (event: any, value: any[]) => {
        setSelectedSites(value);
    };

    if (!vendorId) {
        return (
            <Alert severity="info">
                <Typography variant="body2">
                    {translate('resources.rfid-cards.create.selectVendorFirst') ||
                     'Please select a vendor first to see available sites'}
                </Typography>
            </Alert>
        );
    }

    if (loading) {
        return <Typography variant="body2">Loading sites...</Typography>;
    }

    if (sites.length === 0) {
        return (
            <Alert severity="warning">
                <Typography variant="body2">
                    {translate('resources.rfid-cards.create.noSitesAvailable') ||
                     'No sites are assigned to the selected vendor'}
                </Typography>
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {translate('resources.rfid-cards.create.siteSelectionHelp') ||
                 'Select which sites this card can access. If no sites are selected, the card will have access to all vendor sites.'}
            </Typography>
            <Autocomplete
                multiple
                options={sites}
                value={selectedSites}
                onChange={handleSiteChange}
                loading={loading}
                getOptionLabel={(option) => option.name || ''}
                renderOption={(props, option, { selected }) => (
                    <Box component="li" {...props}>
                        <Checkbox
                            checked={selected}
                            sx={{ mr: 1 }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">
                                {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {[option.addressLine1, option.address, option.city].filter(Boolean).join(', ')}
                            </Typography>
                        </Box>
                    </Box>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            label={option.name}
                            size="small"
                            {...getTagProps({ index })}
                        />
                    ))
                }
                renderInput={(params) => (
                    <MuiTextField
                        {...params}
                        label={translate('resources.rfid-cards.fields.assignedSites') || 'Assigned Sites'}
                        placeholder={loading ? 'Loading sites...' : translate('resources.rfid-cards.create.searchSites') || 'Search and select sites...'}
                        helperText={`${selectedSites.length} ${translate('resources.rfid-cards.siteCount') || 'sites'} selected`}
                    />
                )}
                sx={{
                    '& .MuiAutocomplete-tag': {
                        backgroundColor: '#E8F5E9',
                    },
                }}
                disableCloseOnSelect
                fullWidth
            />
            <input
                type="hidden"
                name="allowedSites"
                value={JSON.stringify(selectedSites.map(s => s.id))}
            />
        </Box>
    );
};

const FormContent = () => {
    const translate = useTranslate();
    const { control } = useFormContext();
    const vendorId = useWatch({ control, name: 'vendorId' });
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const { data: identity } = useGetIdentity();

    // Vendor-scope: restrict the vendor dropdown to the actor's assigned vendors
    // and default a single-vendor actor to that vendor. Super-admins (no vendor
    // membership) see everything.
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;
    const defaultVendorId = isVendorScoped ? actorVendorIds[0] : undefined;

    const validateCardNumber = [
        required(translate('ra.validation.required')),
        minLength(4, translate('ra.validation.minLength', { min: 4 })),
        regex(/^[A-F0-9]+$/, translate('resources.rfid-cards.validation.cardNumberFormat') ||
              'Must contain only hexadecimal characters (0-9, A-F)'),
    ];

    return (
        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
            <PageHeader />

            <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="body2">
                    {translate('resources.rfid-cards.create.infoMessage') ||
                     'RFID cards allow employees to authenticate at charging stations. Each card must be assigned to a user and vendor.'}
                </Typography>
            </Alert>

            {/* Card Information Section */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <CreditCard sx={{ mr: 2, color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('resources.rfid-cards.sections.cardInfo') || 'Card Information'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <TextInput
                            source="cardNumber"
                            label={translate('resources.rfid-cards.fields.cardNumber')}
                            validate={validateCardNumber}
                            fullWidth
                            helperText={translate('resources.rfid-cards.create.cardNumberHelperText') ||
                                      'The unique identifier printed on the RFID card (hexadecimal format)'}
                            sx={{ '& input': { fontFamily: 'monospace', textTransform: 'uppercase' } }}
                        />

                        <SelectInput
                            source="cardType"
                            label={translate('resources.rfid-cards.fields.cardType')}
                            choices={[
                                { id: 'STANDARD', name: translate('resources.rfid-cards.types.standard') || 'Standard' },
                                { id: 'ADMIN', name: translate('resources.rfid-cards.types.admin') || 'Admin' },
                                { id: 'GUEST', name: translate('resources.rfid-cards.types.guest') || 'Guest' },
                                { id: 'FLEET', name: translate('resources.rfid-cards.types.fleet') || 'Fleet' },
                            ]}
                            defaultValue="STANDARD"
                            fullWidth
                            helperText={translate('resources.rfid-cards.create.cardTypeHelperText') ||
                                      'Select the type of access this card provides'}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Assignment Section */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Person sx={{ mr: 2, color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('resources.rfid-cards.sections.assignment') || 'Assignment'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <ReferenceInput
                            source="vendorId"
                            reference="vendors"
                            label={translate('resources.rfid-cards.fields.vendorId')}
                            filter={isVendorScoped ? { id: actorVendorIds } : undefined}
                        >
                            <SelectInput
                                optionText="name"
                                validate={required()}
                                fullWidth
                                defaultValue={defaultVendorId}
                                readOnly={isVendorScoped && actorVendorIds.length === 1}
                                helperText={translate('resources.rfid-cards.create.vendorHelperText') ||
                                          'Select the vendor/company that manages this card'}
                            />
                        </ReferenceInput>

                        <UserSelection
                            vendorId={vendorId}
                            onChange={setSelectedUserId}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Site Access Section */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <LocationCity sx={{ mr: 2, color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('resources.rfid-cards.sections.siteAccess') || 'Site Access'}
                        </Typography>
                    </Box>

                    <SiteSelection vendorId={vendorId} />
                </CardContent>
            </Card>

            {/* Settings Section */}
            <Card sx={{ overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Settings sx={{ mr: 2, color: '#00C853' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('resources.rfid-cards.sections.settings') || 'Settings'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <BooleanInput
                            source="isActive"
                            label={translate('resources.rfid-cards.fields.isActive')}
                            defaultValue={true}
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '1rem',
                                },
                            }}
                        />

                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Info sx={{ fontSize: 20, mr: 1, color: '#1976D2' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {translate('resources.rfid-cards.create.usageInfo') || 'Card Usage'}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {translate('resources.rfid-cards.create.usageHelperText') ||
                                 'Once created, this card can be used immediately at any assigned charging station. Usage history and statistics will be tracked automatically.'}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export const RfidCardsCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    return (
        <Create
            title={false}
            component="div"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapRfidCardBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(rfidCardFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
            transform={data => {
                // Transform the form data to match backend API expectations
                const formData = { ...data };

                // Transform allowedSites to siteIds
                const sitesInput = document.querySelector('input[name="allowedSites"]') as HTMLInputElement;
                if (sitesInput) {
                    const sites = JSON.parse(sitesInput.value || '[]');
                    formData.siteIds = sites.length > 0 ? sites : [];
                    delete formData.allowedSites;
                }

                // The rfidUserId should already be set by the UserSelection component
                // Remove userId if it exists (from old code)
                if (formData.userId) {
                    delete formData.userId;
                }

                return formData;
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <FormContent />
            </SimpleForm>
        </Create>
    );
};